import * as echarts from "echarts";
import { useEffect, useMemo, useRef } from "react";

const PERIOD_CONFIG = {
  NO_PRODUCTIVO: {
    label: "Fuera de horario",
    color: "rgba(148, 163, 184, 0.14)",
    borderColor: "rgba(148, 163, 184, 0.28)",
    legendColor: "#94a3b8",
  },
  PRE_OPERATIVO: {
    label: "Pre operativo",
    color: "rgba(59, 130, 246, 0.12)",
    borderColor: "rgba(59, 130, 246, 0.28)",
    legendColor: "#3b82f6",
  },
  POST_OPERATIVO: {
    label: "Post operativo",
    color: "rgba(139, 92, 246, 0.12)",
    borderColor: "rgba(139, 92, 246, 0.28)",
    legendColor: "#8b5cf6",
  },
  HORA_EXTRA: {
    label: "Hora extra",
    color: "rgba(20, 184, 166, 0.13)",
    borderColor: "rgba(20, 184, 166, 0.3)",
    legendColor: "#14b8a6",
  },
  SUGGESTED_OVERTIME: {
    label: "Posible hora extra",
    color: "rgba(245, 158, 11, 0.15)",
    borderColor: "rgba(245, 158, 11, 0.34)",
    legendColor: "#f59e0b",
  },
};

const MARKER_TYPES = [
  "NO_PRODUCTIVO",
  "PRE_OPERATIVO",
  "POST_OPERATIVO",
  "HORA_EXTRA",
  "SUGGESTED_OVERTIME",
];

const DEVICE_COLORS = [
  "#64748b",
  "#3b82f6",
  "#10b981",
  "#f59e0b",
  "#8b5cf6",
  "#ef4444",
  "#14b8a6",
  "#6366f1",
];

const DEFAULT_TIME_ZONE = "America/Argentina/Buenos_Aires";

const getPeriodLabel = (periodType) => {
  if (periodType === "PRODUCTIVO") return "Horario productivo";
  return PERIOD_CONFIG[periodType]?.label || periodType || "-";
};

const formatDateTime = (value, point, timeZone = DEFAULT_TIME_ZONE) => {
  if (point?.local_timestamp) return point.local_timestamp;

  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleString("es-AR", {
    timeZone,
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
};

const formatTime = (value, timeZone = DEFAULT_TIME_ZONE) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleTimeString("es-AR", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
};

const toNumber = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const isAllowedMarkerType = (type) => Boolean(PERIOD_CONFIG[type]);

const shouldHideProductivePoints = (chartSource) => {
  return chartSource?.scheduleConfigured === true;
};

const getDeviceKey = (point) => {
  return (
    point?.device_mac ||
    point?.mac ||
    point?.serial_number ||
    point?.device_label ||
    point?.device_name ||
    "sin-dispositivo"
  );
};

const getDeviceName = (point) => {
  return (
    point?.device_label ||
    point?.device_name ||
    point?.name ||
    point?.label ||
    point?.device_mac ||
    point?.mac ||
    "Dispositivo"
  );
};

const getEstimatedEndTimestamp = (series, index) => {
  const current = series[index];
  const next = series[index + 1];

  if (next?.timestamp) return next.timestamp;

  const currentTs = current?.timestamp;
  if (!currentTs) return null;

  const currentMs = new Date(currentTs).getTime();
  if (!Number.isFinite(currentMs)) return currentTs;

  if (index > 0) {
    const previousTs = series[index - 1]?.timestamp;
    const previousMs = new Date(previousTs).getTime();

    if (Number.isFinite(previousMs)) {
      const diff = currentMs - previousMs;

      if (diff > 0) {
        return new Date(currentMs + diff).toISOString();
      }
    }
  }

  return currentTs;
};

const dedupeSeriesByTimestampForMarkers = (series = []) => {
  const byTimestamp = new Map();

  series.forEach((point) => {
    if (!point?.timestamp) return;

    if (!byTimestamp.has(point.timestamp)) {
      byTimestamp.set(point.timestamp, point);
    }
  });

  return Array.from(byTimestamp.values()).sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
  );
};

const buildMarkersFromSeries = (series = [], hideProductive = true) => {
  const markers = [];
  const markerSeries = dedupeSeriesByTimestampForMarkers(series);

  let currentType = null;
  let start = null;

  markerSeries.forEach((point, index) => {
    const timestamp = point?.timestamp;
    const periodType = point?.period_type;

    if (!timestamp || !periodType) return;

    if (periodType === "PRODUCTIVO" && hideProductive) {
      if (currentType && start) {
        markers.push({
          type: currentType,
          start,
          end: timestamp,
          label: getPeriodLabel(currentType),
        });
      }

      currentType = null;
      start = null;
      return;
    }

    if (!isAllowedMarkerType(periodType)) {
      if (currentType && start) {
        markers.push({
          type: currentType,
          start,
          end: timestamp,
          label: getPeriodLabel(currentType),
        });
      }

      currentType = null;
      start = null;
      return;
    }

    if (!currentType) {
      currentType = periodType;
      start = timestamp;
      return;
    }

    if (periodType !== currentType) {
      markers.push({
        type: currentType,
        start,
        end: timestamp,
        label: getPeriodLabel(currentType),
      });

      currentType = periodType;
      start = timestamp;
    }

    if (index === markerSeries.length - 1 && currentType && start) {
      markers.push({
        type: currentType,
        start,
        end: getEstimatedEndTimestamp(markerSeries, index),
        label: getPeriodLabel(currentType),
      });
    }
  });

  return markers.filter((marker) => {
    if (!marker.start || !marker.end) return false;

    const startMs = new Date(marker.start).getTime();
    const endMs = new Date(marker.end).getTime();

    return Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs;
  });
};

const normalizePeriodMarkers = (chartSource, hideProductive) => {
  const rawMarkers = Array.isArray(chartSource?.periodMarkers)
    ? chartSource.periodMarkers
    : [];

  if (rawMarkers.length > 0) {
    return rawMarkers
      .map((marker) => {
        const type = marker.type || marker.period_type;

        return {
          type,
          start: marker.start || marker.from,
          end: marker.end || marker.to,
          label: marker.label || getPeriodLabel(type),
        };
      })
      .filter((marker) => {
        if (!marker.type || !marker.start || !marker.end) return false;

        if (hideProductive && marker.type === "PRODUCTIVO") return false;

        const startMs = new Date(marker.start).getTime();
        const endMs = new Date(marker.end).getTime();

        return (
          isAllowedMarkerType(marker.type) &&
          Number.isFinite(startMs) &&
          Number.isFinite(endMs) &&
          endMs > startMs
        );
      });
  }

  return buildMarkersFromSeries(
    Array.isArray(chartSource?.series) ? chartSource.series : [],
    hideProductive,
  );
};

const normalizeSuggestedOvertimeMarkers = (chartSource) => {
  const intervals = chartSource?.suggestedOvertime?.intervals;

  if (!Array.isArray(intervals)) return [];

  return intervals
    .map((interval) => ({
      type: "SUGGESTED_OVERTIME",
      start: interval.start,
      end: interval.end,
      label: "Posible hora extra",
    }))
    .filter((marker) => marker.start && marker.end);
};

const buildMarkAreaData = (markers, type) => {
  return markers
    .filter((marker) => marker.type === type)
    .map((marker) => [
      {
        name: marker.label || getPeriodLabel(type),
        xAxis: marker.start,
      },
      {
        xAxis: marker.end,
      },
    ]);
};

const buildMarkerSeries = (markers) => {
  return MARKER_TYPES.map((type) => {
    const config = PERIOD_CONFIG[type];
    const data = buildMarkAreaData(markers, type);

    if (!config || data.length === 0) return null;

    return {
      name: config.label,
      type: "line",
      yAxisIndex: 0,
      data: [],
      showSymbol: false,
      symbol: "none",
      silent: false,
      lineStyle: {
        color: config.legendColor,
        width: 4,
        opacity: 1,
      },
      itemStyle: {
        color: config.legendColor,
        opacity: 1,
      },
      emphasis: {
        disabled: true,
      },
      markArea: {
        silent: false,
        itemStyle: {
          color: config.color,
          borderColor: config.borderColor,
          borderWidth: 1,
        },
        label: {
          show: false,
        },
        emphasis: {
          itemStyle: {
            color: config.color,
            borderColor: config.borderColor,
            borderWidth: 1,
          },
          label: {
            show: true,
            color: "#334155",
            fontSize: 11,
            fontWeight: 600,
          },
        },
        data,
      },
    };
  }).filter(Boolean);
};

const groupSeriesByDevice = (series = []) => {
  const groups = new Map();

  series.forEach((point) => {
    const key = getDeviceKey(point);
    const name = getDeviceName(point);

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        name,
        points: [],
      });
    }

    groups.get(key).points.push(point);
  });

  return Array.from(groups.values()).map((group) => ({
    ...group,
    points: group.points.sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    ),
  }));
};

const buildDeviceSeries = (deviceGroups) => {
  const series = [];

  deviceGroups.forEach((group, index) => {
    const color = DEVICE_COLORS[index % DEVICE_COLORS.length];

    series.push({
      name: `Consumo real · ${group.name}`,
      type: "bar",
      yAxisIndex: 0,
      barMaxWidth: 16,
      itemStyle: {
        color,
        opacity: 0.6,
        borderRadius: [3, 3, 0, 0],
      },
      emphasis: {
        itemStyle: {
          color,
          opacity: 0.85,
        },
      },
      data: group.points
        .filter((point) => point?.timestamp != null && point.total_energy != null)
        .map((point) => ({
          value: [point.timestamp, toNumber(point.total_energy)],
          point,
          deviceName: group.name,
        })),
      z: 3,
    });
  });

  return series;
};

const buildTooltipRow = (row) => {
  const point = row?.data?.point;
  if (!point) return "";

  const value = Array.isArray(row.data?.value)
    ? row.data.value[1]
    : row.value;

  const deviceName = row.data?.deviceName || getDeviceName(point);

  return `
    <div style="margin-top:8px;padding-top:8px;border-top:1px solid rgba(226,232,240,0.18);">
      <div style="font-weight:700;margin-bottom:4px;">
        ${deviceName}
      </div>

      <div style="display:flex;justify-content:space-between;gap:16px;margin-top:3px;">
        <span>${row.marker} Consumo real</span>
        <strong>${toNumber(value).toFixed(3)} kWh</strong>
      </div>
    </div>
  `;
};

const PassiveNightChart = ({ data, fullData }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  const chartSource = fullData || data;
  const hideProductive = shouldHideProductivePoints(chartSource);
  const timeZone = chartSource?.timeZone || data?.timeZone || DEFAULT_TIME_ZONE;

  const fullSeries = useMemo(() => {
    return Array.isArray(chartSource?.series) ? chartSource.series : [];
  }, [chartSource]);

  const visibleSeries = useMemo(() => {
    const sourceSeries = Array.isArray(data?.series) ? data.series : [];
    const baseSeries = sourceSeries.length > 0 ? sourceSeries : fullSeries;

    if (!hideProductive) {
      return baseSeries;
    }

    return baseSeries.filter((point) => point?.period_type !== "PRODUCTIVO");
  }, [data, fullSeries, hideProductive]);

  const deviceGroups = useMemo(() => {
    return groupSeriesByDevice(visibleSeries);
  }, [visibleSeries]);

  const periodMarkers = useMemo(() => {
    const normalizedMarkers = normalizePeriodMarkers(chartSource, hideProductive);
    const suggestedMarkers = normalizeSuggestedOvertimeMarkers(chartSource);

    return [...normalizedMarkers, ...suggestedMarkers].filter((marker) => {
      if (!isAllowedMarkerType(marker.type)) return false;
      if (hideProductive && marker.type === "PRODUCTIVO") return false;

      return true;
    });
  }, [chartSource, hideProductive]);

  useEffect(() => {
    if (!chartRef.current) return;

    chartInstance.current = echarts.init(chartRef.current);

    const resize = () => chartInstance.current?.resize();
    window.addEventListener("resize", resize);

    return () => {
      window.removeEventListener("resize", resize);
      chartInstance.current?.dispose();
    };
  }, []);

  useEffect(() => {
    if (!chartInstance.current) return;

    if (!visibleSeries.length) {
      chartInstance.current.clear();
      return;
    }

    const markerSeries = buildMarkerSeries(periodMarkers);
    const deviceChartSeries = buildDeviceSeries(deviceGroups);

    const legendData = [
      ...deviceChartSeries.map((serie) => serie.name),
      ...markerSeries.map((serie) => serie.name),
    ].filter((item, index, array) => array.indexOf(item) === index);

    const option = {
      backgroundColor: "#ffffff",
      animation: true,
      grid: {
        left: 56,
        right: 56,
        top: 86,
        bottom: 52,
      },
      tooltip: {
        trigger: "axis",
        confine: true,
        appendToBody: true,
        axisPointer: {
          type: "line",
          lineStyle: {
            color: "#64748b",
            width: 1,
            type: "dashed",
          },
        },
        backgroundColor: "rgba(15, 23, 42, 0.94)",
        borderColor: "rgba(15, 23, 42, 0.94)",
        textStyle: {
          color: "#ffffff",
          fontSize: 12,
        },
        extraCssText:
          "box-shadow: 0 10px 24px rgba(15,23,42,0.18); border-radius: 10px; padding: 10px 12px; max-width: 520px;",
        formatter: (params) => {
          const rows = Array.isArray(params) ? params : [];
          const dataRows = rows.filter((row) => row?.data?.point);

          if (!dataRows.length) return "";

          const firstPoint = dataRows[0].data.point;
          const timestamp =
            firstPoint?.timestamp ||
            (Array.isArray(dataRows[0].data?.value)
              ? dataRows[0].data.value[0]
              : dataRows[0].axisValue);

          const periodLabel = getPeriodLabel(firstPoint?.period_type);
          const reason = firstPoint?.passive_reason;

          const groupedRows = dataRows
            .sort((a, b) => {
              const aName = a?.data?.deviceName || "";
              const bName = b?.data?.deviceName || "";
              return aName.localeCompare(bName);
            })
            .map((row) => buildTooltipRow(row))
            .join("");

          return `
            <div style="min-width:280px;">
              <div style="font-weight:700;margin-bottom:6px;">
                ${formatDateTime(timestamp, firstPoint, timeZone)}
              </div>

              <div style="margin-bottom:4px;">
                Período: <strong>${periodLabel}</strong>
              </div>

              ${
                reason
                  ? `<div style="margin-bottom:6px;color:#cbd5e1;">Motivo: ${reason}</div>`
                  : ""
              }

              ${groupedRows}
            </div>
          `;
        },
      },
      legend: {
        top: 12,
        left: "center",
        type: "scroll",
        itemWidth: 14,
        itemHeight: 8,
        itemGap: 14,
        textStyle: {
          color: "#334155",
          fontSize: 11,
        },
        data: legendData,
      },
      xAxis: {
        type: "time",
        axisLine: {
          lineStyle: {
            color: "#cbd5e1",
          },
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          color: "#64748b",
          hideOverlap: true,
          formatter: (value) => formatTime(value, timeZone),
        },
        splitLine: {
          show: false,
        },
      },
      yAxis: {
        type: "value",
        name: "kWh",
        position: "left",
        nameTextStyle: {
          color: "#64748b",
          fontSize: 11,
        },
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          color: "#94a3b8",
          formatter: (value) => Number(value).toFixed(2),
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: "#e2e8f0",
            type: "solid",
          },
        },
      },
      series: [...markerSeries, ...deviceChartSeries],
    };

    chartInstance.current.setOption(option, true);
  }, [visibleSeries, deviceGroups, periodMarkers, timeZone]);

  return <div ref={chartRef} style={{ width: "100%", height: "380px" }} />;
};

export default PassiveNightChart;